// RinconChatPrototype.js — v4.4
// v4.4 — PSIRT re-run detection: Kelly can trigger a fresh PSIRT query mid-conversation

import { useState, useEffect, useRef, useCallback } from "react";
import { useAuth } from './Auth';
import { supabase } from './supabaseClient';
import UserBadge from './UserBadge';
import {
  LIGHT as C, mono, sans, homeSerif, ACCEPTED_TYPES, MAX_FILE_SIZE,
  preflightCheck, tierLabel, tierColor, renderMarkdown, renderInline,
  TypingDots, BotAvatar as RinconAvatar, UserAvatar, FileUploadCard,
  MessageBubble, ConversationItem, SuggestionChips, DragOverlay, PSIRTProgress,
} from './chat/ChatPrimitives';
import OpsDashboardHome from './chat/OpsDashboardHome';
import FreddyDashboardView from './chat/FreddyDashboardView';

// ─────────────────────────────────────────────
// OPS TOPBAR — shared by the dashboard/Freddy views (reviewers only), styled like the
// JC homepage's own nav: a slim bar, logo left, content right. No sidebar toggle, no
// chat-product chrome — this is the ops side of the page, not the customer chat product.
// ─────────────────────────────────────────────
function OpsTopbar({ crumb, onLogoClick }) {
  return (
    <div style={{ borderBottom:`1px solid ${C.border}`, padding:"14px 24px", display:"flex", alignItems:"center", justifyContent:"space-between", flexShrink:0 }}>
      <button onClick={onLogoClick} style={{ background:"none", border:"none", padding:0, cursor:"pointer", display:"flex", alignItems:"center", gap:8 }}>
        <div style={{ width:22, height:22, background:C.amber, display:"flex", alignItems:"center", justifyContent:"center", borderRadius:5 }}>
          <span style={{ color:"#000", fontFamily:mono, fontSize:11, fontWeight:700 }}>◈</span>
        </div>
        <span style={{ fontFamily:sans, fontSize:14, fontWeight:600, color:C.text }}>netwrkr<span style={{ color:C.amber }}>.ai</span></span>
        <span style={{ color:C.muted, fontFamily:mono, fontSize:11 }}>/ {crumb}</span>
      </button>
      <UserBadge theme={C}/>
    </div>
  );
}

function getJwtClaims() {
  try {
    const raw = localStorage.getItem('netwrkr-auth');
    if (!raw) return {};
    const token = JSON.parse(raw)?.access_token;
    if (!token) return {};
    return JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
  } catch { return {}; }
}

// ─────────────────────────────────────────────
// PSIRT RE-RUN DETECTION
// Matches: "re-run psirt", "check psirt again", "double check the psirt data",
//          "can you verify the psirt", "run psirt again", etc.
// ─────────────────────────────────────────────
function isPsirtRerunRequest(msg) {
  const lower = msg.toLowerCase();
  const hasPsirt = /psirt/.test(lower);
  const hasRerunIntent = /(re.?run|run again|check again|re.?check|double.?check|verify|confirm|refresh|again|repeat)/.test(lower);
  // Also catch "can you check the psirt data" without explicit re-run words
  const isDirectPsirtQuery = /\b(check|verify|query|look up|look at|see)\b.{0,20}psirt/.test(lower);
  return (hasPsirt && hasRerunIntent) || isDirectPsirtQuery;
}

// ─────────────────────────────────────────────
// FABRIC CONTEXT BANNER
// ─────────────────────────────────────────────
function FabricContextBanner({ fabricFile, devices, onClear }) {
  const [expanded, setExpanded] = useState(false);
  if (!fabricFile) return null;
  const tierGroups = {};
  devices.forEach(d => { const t = d.tier||3; if (!tierGroups[t]) tierGroups[t]=[]; tierGroups[t].push(d); });
  return (
    <div style={{ background:C.surface, border:`1px solid ${C.amber}30`, borderLeft:`3px solid ${C.amber}`, borderRadius:16, boxShadow:"0 1px 2px rgba(26,24,16,0.04), 0 8px 20px rgba(26,24,16,0.05)", overflow:"hidden", margin:"0 0 16px", animation:"fadeUp 0.3s ease" }}>
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"10px 14px", cursor:"pointer" }} onClick={() => setExpanded(v=>!v)}>
        <div style={{ display:"flex", alignItems:"center", gap:10 }}>
          <span style={{ color:C.amber, fontFamily:mono, fontSize:12 }}>◈</span>
          <div>
            <div style={{ fontFamily:mono, fontSize:11, color:C.amber, letterSpacing:"0.06em" }}>Fabric context loaded</div>
            <div style={{ fontFamily:mono, fontSize:10, color:C.muted, marginTop:2 }}>{fabricFile.name} · {devices.length} device{devices.length!==1?"s":""} extracted</div>
          </div>
        </div>
        <div style={{ display:"flex", alignItems:"center", gap:10 }}>
          <span style={{ fontFamily:mono, fontSize:10, color:C.muted }}>{expanded?"▲ hide":"▼ show devices"}</span>
          <button onClick={e=>{ e.stopPropagation(); onClear(); }} style={{ background:"none", border:`1px solid rgba(192,57,43,0.3)`, color:"#E07060", fontFamily:mono, fontSize:10, padding:"3px 8px", cursor:"pointer" }}>clear</button>
        </div>
      </div>
      {expanded && (
        <div style={{ borderTop:`1px solid ${C.amber}20`, padding:"12px 14px" }}>
          <div style={{ fontFamily:mono, fontSize:9, color:C.muted, letterSpacing:"0.12em", textTransform:"uppercase", marginBottom:10 }}>Extracted inventory</div>
          {Object.entries(tierGroups).sort(([a],[b])=>Number(a)-Number(b)).map(([tier,devs])=>(
            <div key={tier} style={{ marginBottom:10 }}>
              <div style={{ fontFamily:mono, fontSize:9, color:tierColor(Number(tier), C), marginBottom:5, letterSpacing:"0.1em" }}>// {tierLabel(Number(tier))}</div>
              {devs.map((d,i)=>(
                <div key={i} style={{ display:"flex", alignItems:"center", gap:10, padding:"4px 0", borderBottom:`1px solid ${C.faint}` }}>
                  <div style={{ width:6, height:6, borderRadius:"50%", background:tierColor(Number(tier), C), flexShrink:0 }} />
                  <span style={{ fontFamily:mono, fontSize:12, color:C.text, flex:1 }}>{d.name}</span>
                  {d.version && <span style={{ fontFamily:mono, fontSize:11, color:C.dim }}>v{d.version}</span>}
                  {d.role   && <span style={{ fontFamily:mono, fontSize:10, color:C.muted, background:C.faint, padding:"1px 6px" }}>{d.role}</span>}
                  {!d.version && <span style={{ fontFamily:mono, fontSize:10, color:C.orange }}>no version</span>}
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────
// SUGGESTION CHIPS — local list of Rincon-specific suggestions,
// rendered via the shared SuggestionChips component.
// ─────────────────────────────────────────────
const SUGGESTIONS = [
  "What's the safest upgrade path for a 9.3(x) ACI fabric?",
  "How do I check if a CVE affects my fabric?",
  "What should I do before a major APIC upgrade?",
  "Border leaf BGP best practices for ACI",
  "Upload a config file to analyse your fabric →",
];

// ─────────────────────────────────────────────
// PSIRT QUERY
// ─────────────────────────────────────────────
async function queryPSIRTForDevices(devices, session, onProgress) {
  const devicesWithVersions = devices.filter(d => d.version && d.version.trim());
  const devicesWithout      = devices.filter(d => !d.version || !d.version.trim());
  const isAci               = devices.some(d => d.name && d.name.toUpperCase().includes("APIC"));
  const initialResults = devices.map(d => ({ name:d.name, version:d.version, status:d.version?"pending":"skipped", count:undefined }));
  onProgress({ total:devicesWithVersions.length, done:0, results:initialResults });
  let done = 0;
  const results = [...initialResults];
  const advisoryResults = await Promise.all(
    devicesWithVersions.map(async (device) => {
      const idx = devices.indexOf(device);
      results[idx] = { ...results[idx], status:"querying" };
      onProgress({ total:devicesWithVersions.length, done, results:[...results] });
      try {
        const isAciSwitch = device.isAciSwitch !== undefined ? device.isAciSwitch
          : (isAci && (device.name.toUpperCase().includes("NEXUS")||device.name.toUpperCase().includes("N9K")||device.name.toUpperCase().includes("N7K")||device.name.toUpperCase().includes("N5K")||device.name.toUpperCase().includes("N3K")||device.name.toUpperCase().includes("MDS")));
        const res = await fetch("/api/advisories", {
          method:"POST",
          headers:{ "Content-Type":"application/json", "Authorization":`Bearer ${session?.access_token}` },
          body:JSON.stringify({ platform:device.name, version:device.version, isAciSwitch }),
        });
        const data = await res.json();
        done++;
        const advisoryCount = data.advisories?.length || 0;
        results[idx] = { ...results[idx], status:"done", count:advisoryCount };
        onProgress({ total:devicesWithVersions.length, done, results:[...results] });
        return { device, data, advisoryCount, queryVersion:data.queryVersion||device.version };
      } catch(err) {
        done++;
        results[idx] = { ...results[idx], status:"error" };
        onProgress({ total:devicesWithVersions.length, done, results:[...results] });
        return { device, data:null, advisoryCount:0, error:err.message };
      }
    })
  );
  const timestamp = new Date().toUTCString();
  const lines = [
    `PSIRT check ran: ${timestamp}`,
    `Devices queried: ${devicesWithVersions.length}`,
    "PSIRT ADVISORY RESULTS (live Cisco PSIRT API):",
  ];
  let totalAdvisories = 0;
  for (const { device, data, advisoryCount, queryVersion } of advisoryResults) {
    if (!data || !data.verified) { lines.push(`${device.name} v${device.version}: API query failed or not supported`); continue; }
    totalAdvisories += advisoryCount;
    if (advisoryCount === 0) {
      lines.push(`${device.name} v${device.version} [${data.family||""}]: No advisories found — VERIFIED CLEAN`);
    } else {
      const versionNote = (queryVersion && queryVersion !== device.version) ? ` (queried as v${queryVersion} — ACI NX-OS mapping)` : "";
      lines.push(`${device.name} v${device.version}${versionNote} [${data.family||""}]: ${advisoryCount} advisories`);
      const sorted = [...(data.advisories||[])].sort((a,b)=>({Critical:4,High:3,Medium:2,Low:1}[b.impact]||0)-({Critical:4,High:3,Medium:2,Low:1}[a.impact]||0));
      for (const adv of sorted.slice(0,5)) lines.push(`  - [${adv.impact?.toUpperCase()||"UNKNOWN"}] ${adv.id} — ${adv.title}${adv.firstFixed?` | Fixed: ${adv.firstFixed}`:""}`);
      if (advisoryCount > 5) lines.push(`  - ... and ${advisoryCount-5} more`);
    }
  }
  if (devicesWithout.length > 0) lines.push(`Devices with no version data (not queried): ${devicesWithout.map(d=>d.name).join(", ")}`);
  lines.push(`SUMMARY: ${totalAdvisories} total advisories across ${devicesWithVersions.length} devices queried`);
  return { psirtContext:lines.join("\n"), totalAdvisories, results:advisoryResults };
}

// ─────────────────────────────────────────────
// CHAT GREETING SCREEN
// Props: onPsirtRerun — async () => string|null
//        Returns fresh psirtContext, or null on failure.
//        Called when the user asks to re-check/re-run PSIRT.
// ─────────────────────────────────────────────
function ChatGreeting({ member, greetingData, convId, saveMessage, onViewFull, onPsirtRerun }) {
  const [input, setInput]             = useState('');
  const [chatMsgs, setChatMsgs]       = useState([]);
  const [loading, setLoading]         = useState(false);
  const [localPsirtCtx, setLocalPsirtCtx] = useState(greetingData?.psirtContext || null);
  const bottomRef = useRef(null);

  const firstName = member?.name ? member.name.trim().split(' ')[0] : 'there';
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior:'smooth' }); }, [chatMsgs, loading]);
  // Keep local psirt context in sync if parent updates greetingData
  useEffect(() => { setLocalPsirtCtx(greetingData?.psirtContext || null); }, [greetingData?.psirtContext]);

  const badges = [];
  if (greetingData?.psirtResult?.results) {
    const { results } = greetingData.psirtResult;
    const withAdvisories = results.filter(r=>r.advisoryCount>0).sort((a,b)=>b.advisoryCount-a.advisoryCount);
    if (withAdvisories.length > 0) {
      badges.push({ label:'HIGHEST EXPOSURE', color:C.red, title:withAdvisories[0].device.name, sub:`${withAdvisories[0].advisoryCount} advisories · ${withAdvisories[0].device.role||tierLabel(withAdvisories[0].device.tier)}`, question:`Tell me about the advisories affecting ${withAdvisories[0].device.name} v${withAdvisories[0].device.version}` });
    }
    if (withAdvisories.length > 1) {
      badges.push({ label:'ALSO AFFECTED', color:C.orange, title:withAdvisories[1].device.name, sub:`${withAdvisories[1].advisoryCount} advisories`, question:`What advisories affect ${withAdvisories[1].device.name} v${withAdvisories[1].device.version}?` });
    }
    const versionMismatch = greetingData.devices?.some((d,_,arr)=>d.tier===3&&arr.find(s=>s.tier===2&&s.version!==d.version));
    if (versionMismatch) {
      badges.push({ label:'VERSION DRIFT', color:C.yellow, title:'Spine / leaf mismatch', sub:'Version inconsistency detected', question:'Are there version mismatches between my spine and leaf layers?' });
    } else if (withAdvisories.length === 0) {
      badges.push({ label:'FABRIC STATUS', color:C.green, title:'No advisories found', sub:`${greetingData.devices?.length||0} devices checked`, question:'Give me a summary of my fabric health' });
    }
  }

  const sendGreetingMessage = async (text) => {
    if (!text.trim() || loading) return;
    const userMsg = { role:'user', content:text };
    const newMsgs = [...chatMsgs, userMsg];
    setChatMsgs(newMsgs);
    setInput('');
    setLoading(true);
    if (convId && saveMessage) await saveMessage(convId, 'user', text);

    // ── PSIRT re-run detection ──────────────────────────────────
    if (isPsirtRerunRequest(text) && onPsirtRerun) {
      // Add a status message while the query runs
      setChatMsgs(prev => [...prev, {
        role: 'assistant',
        content: `PSIRT check ran at analysis time — ${greetingData?.devices?.filter(d=>d.version)?.length||0} devices queried. Running a fresh check now to confirm.`
      }]);
      try {
        const freshPsirtCtx = await onPsirtRerun();
        if (freshPsirtCtx) {
          setLocalPsirtCtx(freshPsirtCtx);
          // Replace the holding message with a real Claude response using fresh context
          setChatMsgs(prev => prev.slice(0, -1)); // remove holding message
          const res = await fetch("/api/chat", {
            method:"POST",
            headers:{ "Content-Type":"application/json" },
            body:JSON.stringify({
              fabricContext: greetingData?.fabricContext || null,
              psirtContext:  freshPsirtCtx,
              messages: [...newMsgs, { role:'user', content: text }].map(m=>({ role:m.role==='assistant'?'assistant':'user', content:m.content })),
              systemPromptOverride: buildSystemPromptOverride(greetingData, freshPsirtCtx),
            }),
          });
          const data = await res.json();
          const reply = data.text || 'Unable to retrieve response.';
          setChatMsgs(prev=>[...prev, { role:'assistant', content:reply }]);
          if (convId && saveMessage) await saveMessage(convId, 'assistant', reply);
          setLoading(false);
          return;
        }
      } catch(err) {
        console.error("PSIRT re-run error in greeting:", err);
        // Fall through to normal response
      }
    }
    // ── end PSIRT re-run detection ──────────────────────────────

    try {
      const res = await fetch("/api/chat", {
        method:"POST",
        headers:{ "Content-Type":"application/json" },
        body:JSON.stringify({
          fabricContext: greetingData?.fabricContext || null,
          psirtContext:  localPsirtCtx,
          messages: newMsgs.map(m=>({ role:m.role, content:m.content })),
          systemPromptOverride: buildSystemPromptOverride(greetingData, localPsirtCtx),
        }),
      });
      const data = await res.json();
      const reply = data.text || 'Unable to retrieve response.';
      setChatMsgs(prev=>[...prev, { role:'assistant', content:reply }]);
      if (convId && saveMessage) await saveMessage(convId, 'assistant', reply);
    } catch {
      setChatMsgs(prev=>[...prev, { role:'assistant', content:'Error — please try again.' }]);
    }
    setLoading(false);
  };

  return (
    <div style={{ flex:1, display:'flex', flexDirection:'column', overflow:'hidden', background:C.bg }}>
      <div style={{ flex:1, overflowY:'auto', padding:'40px 24px 16px' }}>
        <div style={{ maxWidth:680, margin:'0 auto' }}>
          <div style={{ marginBottom:32, animation:'fadeUp 0.3s ease' }}>
            <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:16 }}>
              <div style={{ width:26, height:26, background:C.amber, display:'flex', alignItems:'center', justifyContent:'center' }}>
                <span style={{ color:'#000', fontFamily:mono, fontSize:13, fontWeight:700 }}>◈</span>
              </div>
              <span style={{ fontFamily:mono, fontSize:11, color:C.amber }}>JC · DC Expert</span>
            </div>
            <p style={{ fontSize:24, fontWeight:300, color:C.text, letterSpacing:'-0.02em', lineHeight:1.3, marginBottom:8 }}>
              Hey <span style={{ color:C.amber }}>{firstName}</span>, your fabric analysis is ready.
            </p>
            <p style={{ fontFamily:mono, fontSize:12, color:C.muted }}>
              // {greetingData?.devices?.length||0} devices queried · what would you like to know?
            </p>
          </div>

          {greetingData?.licenceWarning && (
            <div style={{ background:greetingData.licenceWarning.includes('all')?`${C.red}10`:`${C.amber}10`, border:`1px solid ${greetingData.licenceWarning.includes('all')?C.red:C.amber}40`, borderLeft:`3px solid ${greetingData.licenceWarning.includes('all')?C.red:C.amber}`, padding:'10px 14px', marginBottom:20, animation:'fadeUp 0.3s ease' }}>
              <div style={{ fontFamily:mono, fontSize:10, color:greetingData.licenceWarning.includes('all')?C.red:C.amber, marginBottom:4, letterSpacing:'0.08em' }}>
                {greetingData.licenceWarning.includes('all')?'⚠ LIMIT REACHED':'⚠ QUOTA WARNING'}
              </div>
              <div style={{ fontSize:13, color:C.dim, lineHeight:1.6 }}>{greetingData.licenceWarning}</div>
              {greetingData.licenceWarning.includes('all') && (
                <button onClick={()=>window.location.href='/settings'} style={{ marginTop:10, background:C.amber, border:'none', color:'#000', fontFamily:mono, fontSize:11, padding:'6px 14px', cursor:'pointer', fontWeight:600 }}>upgrade plan →</button>
              )}
            </div>
          )}

          {badges.length > 0 && chatMsgs.length === 0 && (
            <div style={{ display:'flex', gap:10, flexWrap:'wrap', marginBottom:28, animation:'fadeUp 0.4s ease' }}>
              {badges.map((b,i)=>(
                <button key={i} onClick={()=>sendGreetingMessage(b.question)}
                  style={{ background:'transparent', border:`1px solid ${C.border}`, padding:'12px 16px', cursor:'pointer', flex:1, minWidth:160, textAlign:'left', transition:'border-color 0.15s' }}
                  onMouseEnter={e=>e.currentTarget.style.borderColor=b.color}
                  onMouseLeave={e=>e.currentTarget.style.borderColor=C.border}>
                  <div style={{ fontFamily:mono, fontSize:9, color:b.color, letterSpacing:'0.1em', marginBottom:6 }}>{b.label}</div>
                  <div style={{ fontSize:12, color:C.text, marginBottom:4, fontWeight:500 }}>{b.title}</div>
                  <div style={{ fontFamily:mono, fontSize:10, color:C.muted }}>{b.sub}</div>
                  <div style={{ fontFamily:mono, fontSize:9, color:C.faint, marginTop:8 }}>tap to ask ↗</div>
                </button>
              ))}
            </div>
          )}

          <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
            {chatMsgs.map((msg,i)=>(
              <div key={i} style={{ alignSelf:msg.role==='user'?'flex-end':'flex-start', maxWidth:'85%', background:msg.role==='user'?`${C.amber}12`:C.surface, border:`1px solid ${msg.role==='user'?`${C.amber}25`:C.border}`, borderRadius:msg.role==='user'?'16px 4px 16px 16px':'4px 16px 16px 16px', boxShadow:"0 1px 2px rgba(26,24,16,0.04), 0 8px 20px rgba(26,24,16,0.05)", padding:'12px 16px', fontSize:14, color:C.text, lineHeight:1.75, fontFamily:sans, animation:'fadeUp 0.2s ease' }}>
                {msg.role==='assistant' && <div style={{ fontFamily:mono, fontSize:10, color:C.amber, marginBottom:6 }}>JC · DC Expert</div>}
                {msg.role==='user' ? msg.content : renderMarkdown(msg.content, C)}
              </div>
            ))}
            {loading && (
              <div style={{ alignSelf:'flex-start', background:C.surface, border:`1px solid ${C.border}`, borderRadius:'4px 16px 16px 16px', boxShadow:"0 1px 2px rgba(26,24,16,0.04), 0 8px 20px rgba(26,24,16,0.05)", padding:'14px 18px' }}>
                <div style={{ fontFamily:mono, fontSize:10, color:C.amber, marginBottom:8 }}>JC · thinking</div>
                <TypingDots theme={C}/>
              </div>
            )}
          </div>
          <div ref={bottomRef}/>
        </div>
      </div>

      <div style={{ background:C.hi, borderTop:`1px solid ${C.border}`, padding:'14px 24px', flexShrink:0 }}>
        <div style={{ maxWidth:680, margin:'0 auto' }}>
          <div style={{ background:C.surface, border:`1px solid ${C.border}`, display:'flex', alignItems:'center', gap:8, padding:'10px 14px' }}>
            <span style={{ color:C.muted, fontFamily:mono, fontSize:12, flexShrink:0 }}>→</span>
            <input value={input} onChange={e=>setInput(e.target.value)} onKeyDown={e=>e.key==='Enter'&&sendGreetingMessage(input)}
              placeholder="Ask anything about your fabric…"
              style={{ flex:1, background:'transparent', border:'none', outline:'none', color:C.text, fontSize:14, fontFamily:sans, caretColor:C.amber }}/>
            <button onClick={()=>sendGreetingMessage(input)} disabled={!input.trim()||loading}
              style={{ width:36, height:36, border:'none', flexShrink:0, background:input.trim()&&!loading?C.amber:C.muted, cursor:input.trim()&&!loading?'pointer':'default', display:'flex', alignItems:'center', justifyContent:'center' }}>
              <span style={{ color:'#000', fontSize:15, fontWeight:700 }}>↑</span>
            </button>
            <button onClick={onViewFull}
              style={{ background:'transparent', border:`1px solid ${C.border}`, color:C.muted, fontFamily:mono, fontSize:10, padding:'8px 12px', cursor:'pointer', whiteSpace:'nowrap', transition:'all 0.15s' }}
              onMouseEnter={e=>{ e.currentTarget.style.borderColor=`${C.amber}55`; e.currentTarget.style.color=C.amber; }}
              onMouseLeave={e=>{ e.currentTarget.style.borderColor=C.border; e.currentTarget.style.color=C.muted; }}>
              view full analysis →
            </button>
          </div>
          <div style={{ fontFamily:mono, fontSize:10, color:C.muted, marginTop:6 }}>
            ◈ {greetingData?.devices?.length||0} devices · PSIRT verified · ↵ send · or view full analysis above
          </div>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// SYSTEM PROMPT BUILDER (shared by greeting + main chat)
// ─────────────────────────────────────────────
function buildSystemPromptOverride(greetingData, psirtCtx) {
  return `You are JC, an expert Cisco DC network engineer embedded in netwrkr.ai.
The engineer has just had their fabric analysed. Answer their questions concisely and directly.
Use cautious language for unverified findings. Never say "upgrade immediately" or "critical patch required".
Use "priority review recommended" for unverified findings. Cite exact CSC IDs when available from PSIRT data.
Keep responses to 4-6 sentences. PSIRT-verified findings can use stronger language.

IMPORTANT: A live Cisco PSIRT API check was already run as part of this analysis. The results are below.
Never say you cannot access PSIRT data — you have the results. If the user asks to re-check,
acknowledge the existing result and confirm the re-check ran.

PSIRT DATA:
${psirtCtx || 'No PSIRT data available for this fabric.'}

FABRIC INVENTORY:
${greetingData?.fabricContext || 'No fabric data.'}`;
}

// ─────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────
export default function RinconChatPrototype() {
  const { member, org, session } = useAuth();

  const [conversations, setConversations]       = useState([]);
  const [activeConvId, setActiveConvId]         = useState(null);
  const [messages, setMessages]                 = useState([]);
  const [input, setInput]                       = useState("");
  const [aiTyping, setAiTyping]                 = useState(false);
  const [sidebarOpen, setSidebarOpen]           = useState(true);
  const [loadingHistory, setLoadingHistory]     = useState(true);
  const [fabricContext, setFabricContext]       = useState(null);
  const [fabricDevices, setFabricDevices]       = useState([]);
  const [fabricFile, setFabricFile]             = useState(null);
  const [psirtContext, setPsirtContext]         = useState(null);
  const [psirtProgress, setPsirtProgress]       = useState(null);
  const [uploading, setUploading]               = useState(false);
  const [dragOver, setDragOver]                 = useState(false);
  const [showChatGreeting, setShowChatGreeting] = useState(false);
  const [greetingData, setGreetingData]         = useState(null);
  const [licenceStatus, setLicenceStatus]       = useState(null);
  // Reviewer gating decides which top-level experience renders at all: an ops dashboard
  // (Matt/Andrew) vs. the regular fabric-analysis chat product (every customer). Checked
  // server-side (api/_lib/ledgerReview.js's email allowlist) — see checkReviewerAccess.
  const [isReviewer, setIsReviewer]             = useState(false);
  const [reviewerCheckDone, setReviewerCheckDone] = useState(false);
  const [opsView, setOpsView]                   = useState('dashboard'); // 'dashboard' | 'freddy' | 'chat'
  const [reviewQueueVersion, setReviewQueueVersion] = useState(0);

  const bottomRef   = useRef(null);
  const inputRef    = useRef(null);
  const fileRef     = useRef(null);
  const chatAreaRef = useRef(null);

  const authHeaders = useCallback(() => ({
    "Content-Type": "application/json",
    ...(session?.access_token ? { "Authorization": `Bearer ${session.access_token}` } : {}),
  }), [session?.access_token]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior:"smooth" }); }, [messages, aiTyping]);
  useEffect(() => { if (session) { loadConversations(); loadLicence(); checkReviewerAccess(); } }, [session]);
  useEffect(() => {
    if (activeConvId) {
      setShowChatGreeting(false);
      setGreetingData(null);
      loadMessages(activeConvId);
    } else {
      setMessages([]);
      setFabricContext(null); setFabricDevices([]); setFabricFile(null); setPsirtContext(null);
    }
  }, [activeConvId]);

  // ─────────────────────────────────────────────
  // SUPABASE HELPERS
  // ─────────────────────────────────────────────
  const getOrgMemberIds = async () => {
    const claims = getJwtClaims();
    return { orgId:claims.org_id||org?.id, memberId:claims.member_id||member?.id };
  };

  const loadConversations = async () => {
    setLoadingHistory(true);
    const { data } = await supabase.from('conversations').select('id,title,created_at,updated_at,is_analysis,message_count,has_fabric').order('updated_at',{ascending:false}).limit(50);
    if (data) setConversations(data);
    setLoadingHistory(false);
  };

  const loadMessages = async (convId) => {
    const { data:msgData } = await supabase.from('conversation_messages').select('id,role,content,created_at').eq('conversation_id',convId).order('created_at',{ascending:true});
    if (msgData) setMessages(msgData.map(m=>({ ...m, role:m.role==="assistant"?"ai":m.role })));
    const { data:convData } = await supabase.from('conversations').select('fabric_context,fabric_devices,fabric_psirt_context,has_fabric').eq('id',convId).single();
    if (convData?.has_fabric && convData?.fabric_context) {
      setFabricContext(convData.fabric_context);
      setFabricDevices(convData.fabric_devices || []);
      setPsirtContext(convData.fabric_psirt_context || null);
      const fileNameMatch = convData.fabric_context.match(/^FILE: (.+)$/m);
      setFabricFile({ name:fileNameMatch?fileNameMatch[1]:'fabric-file', size:null, storagePath:null });
    } else {
      setFabricContext(null); setFabricDevices([]); setFabricFile(null); setPsirtContext(null);
    }
  };

  // Reviewer gating lives server-side (an email allowlist in api/_lib/ledgerReview.js) —
  // the frontend just probes the endpoint and decides which top-level experience to render.
  const checkReviewerAccess = async () => {
    try {
      const res = await fetch("/api/ledger-review", { headers:authHeaders() });
      setIsReviewer(res.ok);
    } catch { setIsReviewer(false); }
    setReviewerCheckDone(true);
  };

  const loadLicence = async () => {
    const { orgId } = await getOrgMemberIds();
    if (!orgId) return null;
    const { data } = await supabase.from('licences').select('plan,status,analyses_used,analyses_limit').eq('org_id',orgId).single();
    if (data) setLicenceStatus(data);
    return data;
  };

  const createConversation = async (firstMessage, opts={}) => {
    const { orgId, memberId } = await getOrgMemberIds();
    const title = firstMessage.slice(0,60)+(firstMessage.length>60?"…":"");
    const { data, error } = await supabase.from('conversations').insert({ title, is_analysis:opts.isAnalysis||false, has_fabric:opts.hasFabric||false, message_count:0, org_id:orgId, member_id:memberId }).select('id').single();
    if (error) { console.error('Failed to create conversation:', error); return null; }
    return data.id;
  };

  const saveMessage = async (convId, role, content) => {
    const { orgId } = await getOrgMemberIds();
    const { data, error } = await supabase.from('conversation_messages').insert({ conversation_id:convId, role, content, org_id:orgId }).select('id,role,content,created_at').single();
    if (error) { console.error('Failed to save message:', error); return null; }
    await supabase.rpc('increment_message_count',{ p_conversation_id:convId });
    return data;
  };

  // ─────────────────────────────────────────────
  // PSIRT RE-RUN — called by greeting or main chat
  // Runs a fresh PSIRT query, updates state + DB, returns new context string
  // ─────────────────────────────────────────────
  const handlePsirtRerun = async () => {
    if (!fabricDevices.length || !session) return null;
    const psirtCardId = `psirt-rerun-${Date.now()}`;
    setMessages(prev => [...prev, {
      id: psirtCardId, role: "ai", type: "psirt-progress",
      created_at: new Date().toISOString()
    }]);
    try {
      const psirtResult = await queryPSIRTForDevices(fabricDevices, session, (progress) => {
        setPsirtProgress(progress);
        setMessages(prev => prev.map(m => m.id === psirtCardId ? { ...m, psirtProgress: progress } : m));
      });
      const newPsirtContext = psirtResult.psirtContext;
      setPsirtContext(newPsirtContext);
      // Update greeting data so ChatGreeting gets fresh context too
      setGreetingData(prev => prev ? { ...prev, psirtContext: newPsirtContext, psirtResult } : prev);
      if (activeConvId) {
        await supabase.from('conversations').update({ fabric_psirt_context: newPsirtContext }).eq('id', activeConvId);
      }
      setMessages(prev => prev.map(m => m.id === psirtCardId ? {
        ...m, type: "psirt-done",
        psirtSummary: {
          total: fabricDevices.filter(d => d.version).length,
          advisories: psirtResult.totalAdvisories
        },
        psirtProgress: {
          total: fabricDevices.filter(d => d.version).length,
          done:  fabricDevices.filter(d => d.version).length,
          results: psirtResult.results.map(r => ({
            name: r.device.name, version: r.device.version,
            status: r.error ? "error" : "done", count: r.advisoryCount
          }))
        },
      } : m));
      return newPsirtContext;
    } catch(err) {
      console.error("PSIRT re-run failed:", err);
      setMessages(prev => prev.filter(m => m.id !== psirtCardId));
      return null;
    }
  };

  // ─────────────────────────────────────────────
  // FILE UPLOAD
  // ─────────────────────────────────────────────
  const handleFile = async (file) => {
    if (!file) return;
    if (file.size > MAX_FILE_SIZE) { addLocalMessage("system",`File too large. Maximum size is 5MB.`); return; }
    setUploading(true);

    const uploadCardId = Date.now().toString();
    setMessages(prev=>[...prev,{ id:uploadCardId, role:"user", type:"file", fileData:{ fileName:file.name, fileSize:file.size, status:"uploading" }, created_at:new Date().toISOString() }]);

    try {
      const text = await new Promise((resolve,reject)=>{ const r=new FileReader(); r.onload=e=>resolve(e.target.result); r.onerror=reject; r.readAsText(file); });
      const warnings = preflightCheck(text);
      if (warnings.length > 0) setMessages(prev=>prev.map(m=>m.id===uploadCardId?{...m,fileData:{...m.fileData,status:"preflight",warnings}}:m));
      setMessages(prev=>prev.map(m=>m.id===uploadCardId?{...m,fileData:{...m.fileData,status:"extracting",warnings}}:m));

      const extractRes = await fetch("/api/extract", { method:"POST", headers:authHeaders(), body:JSON.stringify({ text }) });
      const extractData = await extractRes.json();
      if (extractData.error) throw new Error(extractData.message || extractData.error);
      const devices = extractData.devices;

      const { orgId } = await getOrgMemberIds();
      const storagePath = `${orgId}/${Date.now()}-${file.name.replace(/[^a-zA-Z0-9._-]/g,'_')}`;
      const { error:storageError } = await supabase.storage.from('environment-files').upload(storagePath,file,{ contentType:'text/plain', upsert:false });
      if (storageError) console.warn('Storage upload failed (non-fatal):', storageError.message);

      const contextLines = [
        `FILE: ${file.name}`, `DEVICES EXTRACTED: ${devices.length}`, "",
        "FABRIC INVENTORY:",
        ...devices.map(d=>`- ${d.name}${d.version?` v${d.version}`:" (version unknown)"}${d.role?` | ${d.role}`:""} | Tier ${d.tier}`),
        "", "RAW FILE EXCERPT (first 3000 chars):", text.slice(0,3000),
      ].join("\n");

      setFabricContext(contextLines);
      setFabricDevices(devices);
      setFabricFile({ name:file.name, size:file.size, storagePath });
      setMessages(prev=>prev.map(m=>m.id===uploadCardId?{...m,fileData:{...m.fileData,status:"done",warnings}}:m));

      let convId = activeConvId;
      if (!convId) {
        convId = await createConversation(`Fabric analysis: ${file.name}`,{ hasFabric:true });
        if (convId) setActiveConvId(convId);
      }
      if (convId) {
        await supabase.from('conversations').update({ has_fabric:true, fabric_context:contextLines, fabric_devices:devices }).eq('id',convId);
      }
      if (convId) await saveMessage(convId,'user',`[Uploaded fabric file: ${file.name}]`);

      const devicesWithVersions = devices.filter(d=>d.version&&d.version.trim());
      let resolvedPsirtContext = null;
      let psirtResult = null;

      if (devicesWithVersions.length > 0) {
        const psirtCardId = `psirt-${Date.now()}`;
        setMessages(prev=>[...prev,{ id:psirtCardId, role:"ai", type:"psirt-progress", created_at:new Date().toISOString() }]);
        try {
          psirtResult = await queryPSIRTForDevices(devices, session, (progress)=>{
            setPsirtProgress(progress);
            setMessages(prev=>prev.map(m=>m.id===psirtCardId?{...m,psirtProgress:progress}:m));
          });
          resolvedPsirtContext = psirtResult.psirtContext;
          setPsirtContext(resolvedPsirtContext);
          if (convId) await supabase.from('conversations').update({ fabric_psirt_context:resolvedPsirtContext }).eq('id',convId);
          setMessages(prev=>prev.map(m=>m.id===psirtCardId?{
            ...m, type:"psirt-done",
            psirtSummary:{ total:devicesWithVersions.length, advisories:psirtResult.totalAdvisories },
            psirtProgress:{ total:devicesWithVersions.length, done:devicesWithVersions.length, results:psirtResult.results.map(r=>({ name:r.device.name, version:r.device.version, status:r.error?"error":"done", count:r.advisoryCount })) },
          }:m));
        } catch(psirtErr) {
          console.warn("PSIRT query failed (non-fatal):", psirtErr);
          setMessages(prev=>prev.filter(m=>m.id!==psirtCardId));
        }
      }

      await supabase.rpc('increment_analyses_used', { p_org_id: orgId });
      const updatedLicence = await loadLicence();
      const atLimit   = updatedLicence && updatedLicence.analyses_used >= updatedLicence.analyses_limit;
      const nearLimit = updatedLicence && updatedLicence.analyses_used >= updatedLicence.analyses_limit - 2;

      setGreetingData({
        devices,
        psirtResult,
        psirtContext: resolvedPsirtContext,
        fabricContext: contextLines,
        licenceWarning: atLimit
          ? `You have used all ${updatedLicence.analyses_limit} analyses on your ${updatedLicence.plan} plan. Upgrade to continue running analyses.`
          : nearLimit
          ? `You have used ${updatedLicence.analyses_used} of ${updatedLicence.analyses_limit} analyses. ${updatedLicence.analyses_limit - updatedLicence.analyses_used} remaining.`
          : null,
      });
      setShowChatGreeting(true);
      loadConversations();

    } catch(err) {
      console.error('File processing error:', err);
      setMessages(prev=>prev.map(m=>m.id===uploadCardId?{...m,fileData:{...m.fileData,status:"error"}}:m));
    }
    setUploading(false);
  };

  // ─────────────────────────────────────────────
  // CLAUDE API CALL — via /api/chat
  // ─────────────────────────────────────────────
  const callClaude = async (conversationHistory, contextOverride, psirtOverride) => {
    const fabric = contextOverride !== undefined ? contextOverride : fabricContext;
    const psirt  = psirtOverride  !== undefined ? psirtOverride  : psirtContext;
    const res = await fetch("/api/chat", {
      method:"POST", headers:authHeaders(),
      body:JSON.stringify({
        fabricContext: fabric || null,
        psirtContext:  psirt  || null,
        messages: conversationHistory.filter(m=>m.type!=="file").map(m=>({ role:m.role==="ai"?"assistant":"user", content:m.content })),
      }),
    });
    if (!res.ok || !res.body) {
      const data = await res.json().catch(()=>({}));
      throw new Error(data.message || data.error || "Something went wrong");
    }
    // /api/chat streams plain-text deltas (see api/chat.js v1.7+); a reviewer turn instead
    // resolves fully server-side (tool round-trips) and sends one chunk, with any applied
    // fact reviews reported back in the X-Review-Events header so the side panel can refresh.
    const reviewEventsHeader = res.headers.get("X-Review-Events");
    const reader  = res.body.getReader();
    const decoder = new TextDecoder();
    let text = "";
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      text += decoder.decode(value, { stream:true });
    }
    let reviewEvents = null;
    if (reviewEventsHeader) {
      try { reviewEvents = JSON.parse(atob(reviewEventsHeader)); } catch {}
    }
    return { text, reviewEvents };
  };

  // ─────────────────────────────────────────────
  // SEND MESSAGE
  // Includes PSIRT re-run detection: if user asks to re-check PSIRT
  // and fabric devices are loaded, triggers a fresh query first.
  // ─────────────────────────────────────────────
  const send = async (text) => {
    const msg = (text||input).trim();
    if (!msg || aiTyping) return;
    setInput("");

    let convId = activeConvId;
    if (!convId) {
      convId = await createConversation(msg,{ hasFabric:!!fabricContext });
      if (!convId) return;
      setActiveConvId(convId);
    }

    const userMsg  = await saveMessage(convId,"user",msg);
    const localMsg = userMsg || { id:Date.now().toString(), role:"user", content:msg, created_at:new Date().toISOString() };
    setMessages(prev=>[...prev,localMsg]);

    // ── PSIRT re-run detection ──────────────────────────────────
    if (isPsirtRerunRequest(msg) && fabricDevices.length > 0) {
      setAiTyping(true);
      try {
        const freshPsirtCtx = await handlePsirtRerun();
        const history = [...messages, localMsg];
        const { text: aiResponse, reviewEvents } = await callClaude(history, undefined, freshPsirtCtx || psirtContext);
        const savedAiMsg = await saveMessage(convId, "assistant", aiResponse);
        setAiTyping(false);
        setMessages(prev=>[...prev,{
          ...(savedAiMsg||{}),
          id: savedAiMsg?.id||Date.now().toString(),
          role:"ai", content:aiResponse,
          created_at: savedAiMsg?.created_at||new Date().toISOString()
        }]);
        loadConversations();
        if (reviewEvents?.length) setReviewQueueVersion(v=>v+1);
      } catch(err) {
        console.error("PSIRT re-run + chat error:", err);
        setAiTyping(false);
        setMessages(prev=>[...prev,{ id:Date.now().toString(), role:"ai", content:"PSIRT re-query failed. Please try again.", created_at:new Date().toISOString() }]);
      }
      return;
    }
    // ── end PSIRT re-run detection ──────────────────────────────

    setAiTyping(true);
    try {
      const history    = [...messages, localMsg];
      const { text: aiResponse, reviewEvents } = await callClaude(history);
      const savedAiMsg = await saveMessage(convId,"assistant",aiResponse);
      setAiTyping(false);
      setMessages(prev=>[...prev,{ ...(savedAiMsg||{}), id:savedAiMsg?.id||Date.now().toString(), role:"ai", content:aiResponse, created_at:savedAiMsg?.created_at||new Date().toISOString() }]);
      loadConversations();
      if (reviewEvents?.length) setReviewQueueVersion(v=>v+1);
    } catch(err) {
      console.error("Claude error:", err);
      setAiTyping(false);
      setMessages(prev=>[...prev,{ id:Date.now().toString(), role:"ai", content:"I ran into an issue. Please try again.", created_at:new Date().toISOString() }]);
    }
  };

  const addLocalMessage = (role, content) => {
    setMessages(prev=>[...prev,{ id:Date.now().toString(), role, content, created_at:new Date().toISOString() }]);
  };

  const newConversation = () => {
    setActiveConvId(null); setMessages([]); setFabricContext(null);
    setFabricDevices([]); setFabricFile(null); setPsirtContext(null);
    setPsirtProgress(null); setShowChatGreeting(false); setGreetingData(null);
    inputRef.current?.focus();
  };

  const handleDragOver  = (e) => { e.preventDefault(); setDragOver(true); };
  const handleDragLeave = (e) => { if (!chatAreaRef.current?.contains(e.relatedTarget)) setDragOver(false); };
  const handleDrop      = (e) => { e.preventDefault(); setDragOver(false); const f=e.dataTransfer.files[0]; if(f) handleFile(f); };
  const handleKey       = (e) => { if(e.key==="Enter"&&!e.shiftKey){e.preventDefault();send();} };

  const isEmpty = messages.length === 0;

  const globalStyle = (
    <style>{`
      @import url('https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@300;400;500;600;700&family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600;9..40,700&family=Source+Serif+4:opsz,wght@8..60,350;8..60,400&display=swap');
      * { box-sizing:border-box; margin:0; padding:0; }
      ::-webkit-scrollbar{width:4px;} ::-webkit-scrollbar-track{background:transparent;} ::-webkit-scrollbar-thumb{background:${C.border};border-radius:2px;}
      input::placeholder,textarea::placeholder{color:${C.muted};}
      @keyframes fadeUp{from{opacity:0;transform:translateY(8px);}to{opacity:1;transform:translateY(0);}}
      @keyframes dotPulse{0%,100%{transform:scale(1);opacity:0.5;}50%{transform:scale(1.4);opacity:1;}}
      @keyframes spin{to{transform:rotate(360deg);}}
      @keyframes indeterminate{0%{transform:translateX(-100%);}100%{transform:translateX(320%);}}
    `}</style>
  );

  // Reviewer status is unknown for an instant on mount — hold off rendering either
  // experience rather than flashing the customer chat product before swapping to the
  // ops dashboard (or vice versa).
  if (!reviewerCheckDone) {
    return <div style={{ background:C.bg, minHeight:"100vh" }}/>;
  }

  // Ops dashboard — reviewers only (Matt/Andrew). Everyone else always gets the
  // fabric-analysis chat product below, regardless of opsView (customers never set it).
  if (isReviewer && opsView === 'dashboard') {
    return (
      <div style={{ background:C.bg, minHeight:"100vh", display:"flex", flexDirection:"column", fontFamily:sans, color:C.text }}>
        {globalStyle}
        <OpsTopbar crumb="dashboard" onLogoClick={()=>setOpsView('dashboard')}/>
        <OpsDashboardHome member={member} org={org} authHeaders={authHeaders} onOpenFreddy={()=>setOpsView('freddy')} onOpenChat={()=>setOpsView('chat')}/>
      </div>
    );
  }

  if (isReviewer && opsView === 'freddy') {
    return (
      <div style={{ background:C.bg, minHeight:"100vh", display:"flex", flexDirection:"column", fontFamily:sans, color:C.text }}>
        {globalStyle}
        <OpsTopbar crumb="freddy the ledger" onLogoClick={()=>setOpsView('dashboard')}/>
        <FreddyDashboardView authHeaders={authHeaders} version={reviewQueueVersion} onBack={()=>setOpsView('dashboard')} onOpenChat={()=>setOpsView('chat')}/>
      </div>
    );
  }

  // Chat experience — every regular customer lands here directly; a reviewer reaches it
  // via "Ask JC" from the dashboard/Freddy views (opsView === 'chat').
  return (
    <div style={{ background:C.bg, minHeight:"100vh", display:"flex", flexDirection:"column", fontFamily:sans, color:C.text }}>
      {globalStyle}

      {/* TOPBAR */}
      <div style={{ background:C.hi, borderBottom:`1px solid ${C.border}`, padding:"11px 20px", display:"flex", alignItems:"center", justifyContent:"space-between", flexShrink:0, zIndex:10 }}>
        <div style={{ display:"flex", alignItems:"center", gap:12 }}>
          <button onClick={()=>setSidebarOpen(v=>!v)} style={{ background:"none", border:`1px solid ${C.border}`, borderRadius:999, color:C.muted, padding:"5px 8px", cursor:"pointer", fontFamily:mono, fontSize:11 }}>{sidebarOpen?"◧":"▣"}</button>
          <div style={{ display:"flex", alignItems:"center", gap:8 }}>
            <div style={{ width:26, height:26, background:C.amber, display:"flex", alignItems:"center", justifyContent:"center" }}>
              <span style={{ color:"#000", fontFamily:mono, fontSize:13, fontWeight:700 }}>◈</span>
            </div>
            <span style={{ fontFamily:mono, fontSize:13, fontWeight:600, color:C.text }}>netwrkr<span style={{ color:C.amber }}>.ai</span></span>
            {isReviewer ? (
              <button onClick={()=>setOpsView('dashboard')} style={{ background:"none", border:"none", padding:0, color:C.muted, fontFamily:mono, fontSize:11, cursor:"pointer" }}>← dashboard</button>
            ) : (
              <span style={{ color:C.muted, fontFamily:mono, fontSize:11 }}>/ chat</span>
            )}
          </div>
          {fabricFile && (
            <div style={{ display:"flex", alignItems:"center", gap:6, background:C.amberG, border:`1px solid ${C.amber}30`, borderRadius:999, padding:"3px 10px" }}>
              <span style={{ color:C.amber, fontSize:10 }}>◈</span>
              <span style={{ fontFamily:mono, fontSize:10, color:C.amber }}>{fabricFile.name} · {fabricDevices.length} devices</span>
            </div>
          )}
        </div>
        <div style={{ display:"flex", alignItems:"center", gap:10 }}>
          <div style={{ width:1, height:20, background:C.border }}/>
          <UserBadge theme={C}/>
        </div>
      </div>

      {/* MAIN LAYOUT */}
      <div style={{ flex:1, display:"flex", overflow:"hidden", minHeight:0 }}>

        {/* SIDEBAR */}
        {sidebarOpen && (
          <div style={{ width:260, flexShrink:0, background:C.surface, borderRight:`1px solid ${C.border}`, display:"flex", flexDirection:"column", overflow:"hidden" }}>
            <div style={{ padding:"14px 16px 10px", borderBottom:`1px solid ${C.border}` }}>
              <div style={{ fontFamily:mono, fontSize:9, color:C.muted, letterSpacing:"0.15em", textTransform:"uppercase", marginBottom:10 }}>// conversations</div>
              <button onClick={newConversation} style={{ width:"100%", background:C.amberG, border:`1px solid ${C.amber}30`, borderRadius:999, color:C.amber, fontFamily:mono, fontSize:11, padding:"8px", cursor:"pointer" }}>+ new_chat()</button>
            </div>
            <div style={{ flex:1, overflowY:"auto" }}>
              {loadingHistory ? (
                <div style={{ padding:"20px 16px", fontFamily:mono, fontSize:11, color:C.muted }}>Loading…</div>
              ) : conversations.length===0 ? (
                <div style={{ padding:"20px 16px", fontFamily:mono, fontSize:11, color:C.muted }}>// no conversations yet</div>
              ) : (
                <>
                  {conversations.some(c=>c.has_fabric) && (<>
                    <div style={{ padding:"12px 16px 6px", fontFamily:mono, fontSize:9, color:C.muted, letterSpacing:"0.12em", textTransform:"uppercase" }}>With fabric context</div>
                    {conversations.filter(c=>c.has_fabric).map(conv=><ConversationItem key={conv.id} conv={conv} active={conv.id===activeConvId} onClick={()=>setActiveConvId(conv.id)} theme={C}/>)}
                  </>)}
                  {conversations.some(c=>!c.has_fabric&&!c.is_analysis) && (<>
                    <div style={{ padding:"12px 16px 6px", fontFamily:mono, fontSize:9, color:C.muted, letterSpacing:"0.12em", textTransform:"uppercase" }}>Conversations</div>
                    {conversations.filter(c=>!c.has_fabric&&!c.is_analysis).map(conv=><ConversationItem key={conv.id} conv={conv} active={conv.id===activeConvId} onClick={()=>setActiveConvId(conv.id)} theme={C}/>)}
                  </>)}
                </>
              )}
            </div>
            <div style={{ padding:"12px 16px", borderTop:`1px solid ${C.border}` }}>
              <div style={{ background:C.surface, border:`1px solid ${C.amber}20`, borderRadius:14, boxShadow:"0 1px 2px rgba(26,24,16,0.04), 0 8px 20px rgba(26,24,16,0.05)", padding:"10px 12px", marginBottom:10 }}>
                <div style={{ fontFamily:mono, fontSize:9, color:C.amber, marginBottom:4, letterSpacing:"0.1em" }}>// enterprise</div>
                <div style={{ fontSize:11, color:C.muted, lineHeight:1.5, marginBottom:8 }}>Connect directly to APIC or Nexus Dashboard — no file upload needed.</div>
                <button style={{ width:"100%", background:"none", border:`1px solid ${C.amber}30`, borderRadius:999, color:C.amber, fontFamily:mono, fontSize:10, padding:"5px", cursor:"not-allowed", opacity:0.5 }}>🔒 connect_apic() — enterprise</button>
              </div>
              <button onClick={()=>window.location.href='/history'} style={{ width:"100%", background:"none", border:`1px solid ${C.border}`, borderRadius:999, color:C.muted, fontFamily:mono, fontSize:10, padding:"7px", cursor:"pointer" }} onMouseEnter={e=>{e.currentTarget.style.borderColor=`${C.amber}44`;e.currentTarget.style.color=C.dim;}} onMouseLeave={e=>{e.currentTarget.style.borderColor=C.border;e.currentTarget.style.color=C.muted;}}>
                view_full_history() →
              </button>
            </div>
          </div>
        )}

        {/* CHAT AREA */}
        <div ref={chatAreaRef} style={{ flex:1, display:"flex", flexDirection:"column", overflow:"hidden", minWidth:0, position:"relative" }} onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop}>
          <DragOverlay visible={dragOver} theme={C}/>

          {showChatGreeting ? (
            <ChatGreeting
              member={member}
              greetingData={greetingData}
              convId={activeConvId}
              saveMessage={saveMessage}
              onViewFull={() => setShowChatGreeting(false)}
              onPsirtRerun={handlePsirtRerun}
            />
          ) : (
            <>
              <div style={{ flex:1, overflowY:"auto", padding:"32px 24px 24px" }}>
                <div style={{ maxWidth:740, margin:"0 auto" }}>
                  {isEmpty && (
                    <div style={{ animation:"fadeUp 0.4s ease" }}>
                      <div style={{ marginBottom:32, paddingTop:20 }}>
                        <div style={{ display:"inline-flex", alignItems:"center", gap:10, background:C.amberG, border:`1px solid ${C.amber}30`, borderRadius:999, padding:"8px 16px", marginBottom:24 }}>
                          <span style={{ color:C.amber, fontSize:16 }}>◈</span>
                          <span style={{ fontFamily:mono, fontSize:11, color:C.amber, letterSpacing:"0.08em" }}>JC · DC Expert</span>
                        </div>
                        <h1 style={{ fontSize:32, fontWeight:400, letterSpacing:"-0.01em", lineHeight:1.2, marginBottom:14, color:C.text, fontFamily:homeSerif }}>What does your fabric<br/><span style={{ color:C.amber }}>need to know?</span></h1>
                        <p style={{ fontSize:14, color:C.dim, lineHeight:1.8, maxWidth:480 }}>Ask anything about your Cisco DC fabric, or upload a config file to ground the conversation in your actual inventory.</p>
                      </div>
                      <div onClick={()=>fileRef.current?.click()} style={{ border:`1px dashed ${C.amber}40`, borderRadius:16, padding:"20px 24px", marginBottom:24, cursor:"pointer", transition:"all 0.2s", background:C.surface, boxShadow:"0 1px 2px rgba(26,24,16,0.04), 0 16px 40px rgba(26,24,16,0.06)", display:"flex", alignItems:"center", gap:16 }} onMouseEnter={e=>e.currentTarget.style.borderColor=`${C.amber}80`} onMouseLeave={e=>e.currentTarget.style.borderColor=`${C.amber}40`}>
                        <div style={{ fontSize:24, color:C.amber }}>↑</div>
                        <div>
                          <div style={{ fontFamily:mono, fontSize:12, color:C.amber, marginBottom:4 }}>upload_fabric_file()</div>
                          <div style={{ fontSize:13, color:C.muted }}>Drop a config file, show version output, or CSV inventory. JC will parse it and answer questions about your specific fabric.</div>
                          <div style={{ fontFamily:mono, fontSize:10, color:C.faint, marginTop:6 }}>.txt .log .csv .conf .cfg .json · max 5MB · drag and drop supported</div>
                        </div>
                      </div>
                      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:10, marginBottom:24 }}>
                        {[["↑","Upgrade sequencing","Safe order for your fabric tier by tier"],["⚠","Bug research","Known issues for your platform + version"],["⟳","Version compatibility","APIC ↔ leaf ↔ spine alignment"]].map(([icon,title,desc])=>(
                          <div key={title} style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:16, boxShadow:"0 1px 2px rgba(26,24,16,0.04), 0 16px 40px rgba(26,24,16,0.06)", padding:"14px 16px" }}>
                            <div style={{ fontFamily:mono, fontSize:16, color:C.amber, marginBottom:8 }}>{icon}</div>
                            <div style={{ fontSize:13, fontWeight:500, color:C.text, marginBottom:4 }}>{title}</div>
                            <div style={{ fontSize:12, color:C.muted, lineHeight:1.5 }}>{desc}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {fabricFile && <FabricContextBanner fabricFile={fabricFile} devices={fabricDevices} onClear={()=>{ setFabricContext(null); setFabricDevices([]); setFabricFile(null); setPsirtContext(null); setPsirtProgress(null); }}/>}
                  {messages.map(msg=><MessageBubble key={msg.id} msg={msg} member={member} theme={C} personaName="JC"/>)}

                  {aiTyping && (
                    <div style={{ display:"flex", gap:14, marginTop:20 }}>
                      <RinconAvatar theme={C}/>
                      <div style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:"4px 16px 16px 16px", boxShadow:"0 1px 2px rgba(26,24,16,0.04), 0 8px 20px rgba(26,24,16,0.05)", padding:"14px 18px" }}>
                        <div style={{ fontFamily:mono, fontSize:11, color:C.amber, marginBottom:8 }}>JC · thinking</div>
                        <TypingDots theme={C}/>
                      </div>
                    </div>
                  )}
                  <div ref={bottomRef}/>
                </div>
              </div>

              {isEmpty && <SuggestionChips suggestions={SUGGESTIONS} onSelect={s=>{ setInput(s); inputRef.current?.focus(); }} onUpload={()=>fileRef.current?.click()} theme={C}/>}

              {/* INPUT BAR */}
              <div style={{ background:C.bg, borderTop:`1px solid ${C.border}`, padding:"14px 24px", flexShrink:0 }}>
                <div style={{ maxWidth:740, margin:"0 auto" }}>
                  <div style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:22, boxShadow:"0 1px 2px rgba(26,24,16,0.04), 0 16px 40px rgba(26,24,16,0.06)", display:"flex", alignItems:"flex-end", gap:8, padding:"10px 10px 10px 16px" }}>
                    <button onClick={()=>fileRef.current?.click()} disabled={uploading} title="Upload fabric file" style={{ background:"none", border:`1px solid ${C.border}`, borderRadius:"50%", width:30, height:30, color:uploading?C.amber:C.muted, cursor:"pointer", flexShrink:0, fontSize:14, display:"flex", alignItems:"center", justifyContent:"center", transition:"color 0.15s" }} onMouseEnter={e=>e.currentTarget.style.color=C.amber} onMouseLeave={e=>e.currentTarget.style.color=uploading?C.amber:C.muted}>
                      {uploading?<span style={{ display:"inline-block", animation:"spin 0.7s linear infinite" }}>⟳</span>:"↑"}
                    </button>
                    <textarea ref={inputRef} value={input} onChange={e=>setInput(e.target.value)} onKeyDown={handleKey} placeholder={fabricFile?`Ask about ${fabricFile.name}…`:"Ask JC anything about your Cisco DC fabric…"} disabled={aiTyping} rows={1}
                      style={{ flex:1, background:"transparent", border:"none", outline:"none", color:C.text, fontSize:14, fontFamily:sans, resize:"none", lineHeight:1.5, maxHeight:120, overflowY:"auto", caretColor:C.amber, padding:"5px 0" }}
                      onInput={e=>{ e.target.style.height="auto"; e.target.style.height=Math.min(e.target.scrollHeight,120)+"px"; }}/>
                    <button onClick={()=>send()} disabled={!input.trim()||aiTyping} style={{ width:30, height:30, borderRadius:"50%", border:"none", flexShrink:0, background:input.trim()&&!aiTyping?C.amber:C.border, cursor:input.trim()&&!aiTyping?"pointer":"default", display:"flex", alignItems:"center", justifyContent:"center", transition:"background 0.15s" }}>
                      <span style={{ color:"#FFF", fontSize:14, fontWeight:700 }}>↑</span>
                    </button>
                  </div>
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginTop:6, paddingLeft:2 }}>
                    <span style={{ fontFamily:mono, fontSize:10, color:C.muted }}>{fabricFile?`◈ Fabric context: ${fabricFile.name} · ${fabricDevices.length} devices`:"JC · Cisco DC Expert · powered by Claude"}</span>
                    <span style={{ fontFamily:mono, fontSize:10, color:C.muted }}>↑ attach file · ↵ send</span>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      <input ref={fileRef} type="file" accept={ACCEPTED_TYPES.join(',')} style={{ display:"none" }} onChange={e=>{ const f=e.target.files[0]; if(f) handleFile(f); e.target.value=""; }}/>
    </div>
  );
}